/**
 * Display formatting for the build version.
 *
 * The deploy workflows do not produce a display-ready string. Nonprod builds
 * (`app-deploy-nonprod.yml`) stamp `${ENV}-v${SEMVER}-${SHA8}` and production
 * (`app-deploy-production-gke.yml`) stamps `${TAG#prod-}-${SHA8}`, so the raw
 * value carries the environment, the semver and a commit sha in one token:
 *
 *   beta-v1.1.0-a1b2c3d4   alpha-v1.1.0-a1b2c3d4   v1.1.0-51ec73e6   dev
 *
 * Callers want the version and the environment, not the sha — so this reorders
 * to `v<semver>-<env>` and drops the sha. Production has no environment prefix
 * and renders as a bare `v<semver>`.
 */

/** Matches the shapes the workflows emit; anything else falls through. */
const VERSION_RE =
  /^(?:([a-z]+)-)?v(\d+\.\d+\.\d+)(?:-([0-9a-f]{6,}))?$/i;

/**
 * Turn a raw VITE_APP_VERSION into something worth showing a human.
 *
 *   beta-v1.1.0-a1b2c3d4  ->  v1.1.0-beta
 *   v1.1.0-51ec73e6       ->  v1.1.0          (production: no env prefix)
 *   dev                   ->  dev             (local build, no stamp)
 *
 * Unrecognised input is returned unchanged rather than mangled or blanked — a
 * version we cannot parse is still more useful on screen than nothing, and
 * silently hiding it would make a broken build stamp invisible.
 */
export function formatAppVersion(raw?: string): string {
  const value = (raw ?? '').trim();
  if (!value) return 'dev';

  const m = VERSION_RE.exec(value);
  if (!m) return value;

  const [, env, semver] = m;
  return env ? `v${semver}-${env.toLowerCase()}` : `v${semver}`;
}

/**
 * The formatted version for this build.
 *
 * Note the value already carries its own `v`, so callers must render it as-is.
 * Prefixing another one is the bug this replaced: build-info-widget rendered
 * `v{version}` against a string starting with `v`, so beta showed
 * `vbeta-v1.1.0-a1b2c3d4`.
 */
export function appVersion(): string {
  return formatAppVersion(import.meta.env.VITE_APP_VERSION);
}
